import { getAppTitle, getAppDescription, getSiteUrl } from '../utils/config';
import { Locale } from '../config/seoPages';

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildOrganizationSchema(locale: Locale) {
  const siteUrl = getSiteUrl();
  const name = getAppTitle();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url: siteUrl,
    description: getAppDescription(),
    logo: `${siteUrl}/logo192.png`,
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: locale === 'zh' ? 'customer support' : 'customer support',
      availableLanguage: ['Chinese', 'English'],
    },
  };
}

export function buildWebSiteSchema(locale: Locale) {
  const siteUrl = getSiteUrl();
  const name = getAppTitle();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url: siteUrl,
    inLanguage: locale === 'zh' ? 'zh-CN' : 'en',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}${locale === 'en' ? '/en' : ''}/help?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildBreadcrumbSchema(
  items: { name: string; path: string }[],
  locale: Locale
) {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.path.startsWith('/en') || locale === 'en' ? item.path : item.path}`,
    })),
  };
}

export function buildFaqSchema(faqs: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function buildProductOfferSchema(
  plans: { name: string; price: string; description: string }[],
  locale: Locale
) {
  const siteUrl = getSiteUrl();
  const brand = getAppTitle();
  return plans.map((plan) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${brand} - ${plan.name}`,
    description: plan.description,
    brand: { '@type': 'Brand', name: brand },
    offers: {
      '@type': 'Offer',
      price: plan.price.replace(/[^\d.]/g, '') || '0',
      priceCurrency: locale === 'zh' ? 'CNY' : 'USD',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}${locale === 'en' ? '/en' : ''}/pricing`,
    },
  }));
}
